/**
 * App-level tuning (app.json, Zod-validated). `quality` holds the render values the app itself applies
 * (pixel ratio cap and LOD bias, spec §8); G12's quality module takes these over with the full preset table.
 * M2 (plan §2.4): `defaultMode` (the game mode without `?mode=`) and `deferredAssetPrefixes` (asset ids boot skips;
 * the career feature loads them after the first frame).
 * M3 bridge (R22d, plan §1.2.1, §7 item 11): `careerInReach` switches the unified career on: the career runs in the
 * Reach (`ctx.reach` non-null) and `?mode=reach` becomes an alias of `career`. The schema default is off; app.json
 * turns it on since D-245 (the career plays across the Reach).
 */
import { z } from 'zod'
import type { ShipStreamConfig } from '../assets/shipStream'
import type { QualityPreset } from '../render/contracts'
import appJson from './app.json'
import type { SimSystems } from '../sim'
import { GAME_MODES, type GameMode } from './feature'

const qualityRowSchema = z.object({
  /** Pixel ratio = min(devicePixelRatio, maxPixelRatio). */
  maxPixelRatio: z.number().positive().max(4),
  /** Scales every model LOD switch distance for new instances. */
  lodBias: z.number().positive().max(2),
})

export const appConfigSchema = z.object({
  views: z.object({
    /** A hit that removes this fraction of the pool it struck (shield or hull maximum) shows at full strength. */
    hitFullStrength: z.number().positive().max(1),
  }),
  quality: z.object({ low: qualityRowSchema, medium: qualityRowSchema, high: qualityRowSchema }),
  /** The game mode when the URL has no valid `?mode=` (M2-22 flips it to `career`). */
  defaultMode: z.enum(GAME_MODES),
  /**
   * Asset-id prefixes that boot skips. They load later through `ctx.loader` (M2: stations and props after the
   * first frame, career mode only; M3: `reach.` models per system).
   */
  deferredAssetPrefixes: z.array(z.string().regex(/^[a-z][a-z0-9_]*\.$/)).default([]),
  /**
   * M3 bridge (R22d, D-TBD: career in the Reach behind a switch): the career runs in the Reach and `?mode=reach` is
   * an alias of `career`. On in app.json since D-245; while off (the schema default, or `?careerInReach=0` in test
   * builds), `reach` is M3 with its stand-ins.
   */
  careerInReach: z.boolean().default(false),
})

export type AppConfig = z.infer<typeof appConfigSchema>

export function loadAppConfig(): AppConfig {
  return appConfigSchema.parse(appJson)
}

export function pixelRatioFor(config: AppConfig, preset: QualityPreset, devicePixelRatio: number): number {
  return Math.min(devicePixelRatio, config.quality[preset].maxPixelRatio)
}

function isGameMode(value: string | null): value is GameMode {
  return value !== null && (GAME_MODES as readonly string[]).includes(value)
}

/** The config a mode decision reads: `careerInReach` absent counts as off. */
export type ModeConfig = Pick<AppConfig, 'defaultMode'> & Partial<Pick<AppConfig, 'careerInReach'>>

/**
 * `?mode=career|sandbox|reach` from a query string, else `config.defaultMode` (an unknown value falls back too).
 * With `careerInReach` on, `reach` (asked for or the default) is an alias of `career`.
 */
export function parseGameMode(search: string, config: ModeConfig): GameMode {
  const param = new URLSearchParams(search).get('mode')
  const mode = isGameMode(param) ? param : config.defaultMode
  return mode === 'reach' && config.careerInReach === true ? 'career' : mode
}

/**
 * Test builds only (R22l): `?careerInReach=1` (or `0`) overrides `careerInReach` for one page, so the bridge e2e runs
 * the career in the Reach while app.json keeps the switch off until the landing flips it. Any other value is ignored.
 */
export function withCareerInReachOverride(config: AppConfig, search: string): AppConfig {
  const value = new URLSearchParams(search).get('careerInReach')
  if (value !== '1' && value !== '0') return config
  return { ...config, careerInReach: value === '1' }
}

/** True when `mode` boots the Reach runtime (`ctx.reach` non-null): `reach`, and `career` once `careerInReach` is on. */
export function runsInReach(mode: GameMode, config: Partial<Pick<AppConfig, 'careerInReach'>>): boolean {
  return mode === 'reach' || (mode === 'career' && config.careerInReach === true)
}

/**
 * The sim's systems for a mode (`createSim` `systems`). In the Reach (no preset field: the runtime streams
 * `sim.field`) AI pilots run and the M1 waves do not; the career in the Reach also leaves respawn to M2's death flow.
 * Outside the Reach: the career turns the waves and respawn off, and the sandbox is M1 exactly (undefined).
 */
export function simSystemsFor(mode: GameMode, inReach: boolean): Partial<SimSystems> | undefined {
  if (inReach) return mode === 'career' ? { ai: true, waves: false, respawn: false } : { ai: true, waves: false }
  return mode === 'career' ? { waves: false, respawn: false } : undefined
}

/** True when boot skips asset `id` (it starts with a `deferredAssetPrefixes` entry). */
export function isDeferredAsset(config: Pick<AppConfig, 'deferredAssetPrefixes'>, id: string): boolean {
  const prefixes = config.deferredAssetPrefixes
  for (let i = 0; i < prefixes.length; i++) if (id.startsWith(prefixes[i])) return true
  return false
}

/** The `ship-stream.json` fields the boot filter reads (M10 P1b1). */
export type ShipStreamBootConfig = Pick<ShipStreamConfig, 'enabled' | 'bootShips' | 'streamedShips'>

/**
 * True when boot loads asset `id` (M10 P1b1, hull tiers spec §6.5): not deferred, not in `streamedShips` (any mode),
 * and, with streaming `enabled`, not a `ship.` model outside `bootShips`, except in the M1 sandbox, which keeps every
 * ship at boot (its waves spawn raiders from boot's models). The ship stream loads what boot skips.
 */
export function isBootAsset(config: Pick<AppConfig, 'deferredAssetPrefixes'>, stream: ShipStreamBootConfig, id: string, mode: GameMode): boolean {
  if (isDeferredAsset(config, id)) return false
  if (stream.streamedShips.includes(id)) return false
  if (stream.enabled && mode !== 'sandbox' && id.startsWith('ship.') && !stream.bootShips.includes(id)) return false
  return true
}
